import { KB_STAGES, type SolutionListItem } from '@loop3/universe'
import { SOLUTIONS_LIBRARY_PATH } from '@/lib/routes'

export interface LibraryFilters {
  q: string
  type: 'all' | 'solution' | 'feature'
  client: string | null
  vertical: string | null
  capability: string | null
  stage: string | null
  lifecycle: string | null
  reusable: boolean
}

export const FILTER_PARAMS: readonly (keyof LibraryFilters)[] = [
  'q',
  'type',
  'client',
  'vertical',
  'capability',
  'stage',
  'lifecycle',
  'reusable',
]

const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()

/** Library filters from the URL; unknown or missing values fall back to "no filter". */
export function readFilters(params: URLSearchParams): LibraryFilters {
  const type = params.get('type')
  return {
    q: params.get('q') ?? '',
    type: type === 'solution' || type === 'feature' ? type : 'all',
    client: params.get('client'),
    vertical: params.get('vertical'),
    capability: params.get('capability'),
    stage: params.get('stage'),
    lifecycle: params.get('lifecycle'),
    reusable: params.get('reusable') === '1',
  }
}

/** A copy of `params` with one filter set; default values remove the param. */
export function writeFilter(
  params: URLSearchParams,
  key: keyof LibraryFilters,
  value: string | boolean | null
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (key === 'reusable') {
    if (value === true) next.set('reusable', '1')
    else next.delete('reusable')
  } else if (
    typeof value === 'string' &&
    value &&
    !(key === 'type' && value === 'all')
  ) {
    next.set(key, value)
  } else {
    next.delete(key)
  }
  return next
}

/** The solutions library path with the given filters in its query string. */
export function solutionsLibraryHref(filters: Partial<LibraryFilters>): string {
  const params = Object.entries(filters).reduce(
    (acc, [key, value]) => writeFilter(acc, key as keyof LibraryFilters, value),
    new URLSearchParams()
  )
  const query = params.toString()
  return query ? `${SOLUTIONS_LIBRARY_PATH}?${query}` : SOLUTIONS_LIBRARY_PATH
}

/** True when any filter other than the defaults is set. */
export function hasFilters(f: LibraryFilters): boolean {
  return (
    !!norm(f.q) ||
    f.type !== 'all' ||
    !!f.client ||
    !!f.vertical ||
    !!f.capability ||
    !!f.stage ||
    !!f.lifecycle ||
    f.reusable
  )
}

/** Rows matching every set filter; the text search looks at name, summary, client and capabilities. */
export function filterSolutions(
  items: SolutionListItem[],
  f: LibraryFilters
): SolutionListItem[] {
  const q = norm(f.q)
  return items.filter(
    (s) =>
      (f.type === 'all' || s.type === f.type) &&
      (!f.client || s.client?.prefix === f.client) &&
      (!f.vertical || s.verticals.includes(f.vertical)) &&
      (!f.capability || s.capabilities.includes(f.capability)) &&
      (!f.stage || s.stages.includes(f.stage)) &&
      (!f.lifecycle || s.lifecycle === f.lifecycle) &&
      (!f.reusable || s.reusable) &&
      (!q ||
        norm(
          [s.name, s.summary, s.client?.name ?? '', ...s.capabilities].join(' ')
        ).includes(q))
  )
}

export interface FacetOptions {
  clients: { prefix: string; name: string }[]
  verticals: string[]
  capabilities: string[]
  lifecycles: string[]
}

const distinct = (values: string[]) =>
  [...new Set(values)].sort((a, b) => a.localeCompare(b))

/** The values each select offers, taken from the loaded rows. */
export function facetOptions(items: SolutionListItem[]): FacetOptions {
  const clients = new Map(
    items.flatMap((s) =>
      s.client ? [[s.client.prefix, s.client] as const] : []
    )
  )
  return {
    clients: [...clients.values()].sort((a, b) => a.name.localeCompare(b.name)),
    verticals: distinct(items.flatMap((s) => s.verticals)),
    capabilities: distinct(items.flatMap((s) => s.capabilities)),
    lifecycles: distinct(
      items.flatMap((s) => (s.lifecycle ? [s.lifecycle] : []))
    ),
  }
}

const STALE_AFTER_MS = 3 * 60 * 60 * 1000

/** "Last updated" label for the newest sync, and whether it is older than 3 hours; null before the first run. */
export function syncFreshness(
  finishedAt: string | null,
  now: Date
): { label: string; stale: boolean } | null {
  if (!finishedAt) return null
  const ms = now.getTime() - new Date(finishedAt).getTime()
  if (Number.isNaN(ms)) return null
  const min = Math.floor(ms / 60_000)
  const label =
    min < 1
      ? 'just now'
      : min < 60
        ? `${min} min ago`
        : min < 48 * 60
          ? `${Math.floor(min / 60)} h ago`
          : `${Math.floor(min / 1440)} d ago`
  return { label, stale: ms > STALE_AFTER_MS }
}

/** How many of the given solutions sit in each knowledge-loop stage; zero marks a gap. */
export function stageCoverage(
  items: SolutionListItem[]
): { stage: string; count: number }[] {
  return KB_STAGES.map((stage) => ({
    stage,
    count: items.filter((s) => s.stages.includes(stage)).length,
  }))
}

// The sync takes ~2 minutes; past 10 the run has failed or is stuck, so stop waiting.
export const REFRESH_MAX_WAIT_MS = 10 * 60_000

/**
 * Where a "Refresh now" request stands. `baseline` is the last sync seen at click time, so done
 * means a strictly newer sync landed (server timestamps only, no browser/server clock skew).
 */
export function refreshState(
  request: { at: number; baseline: string | null } | null,
  lastSync: string | null,
  now: number
): 'idle' | 'waiting' | 'done' | 'timed-out' {
  if (!request) return 'idle'
  const synced = lastSync ? Date.parse(lastSync) : NaN
  const base = request.baseline ? Date.parse(request.baseline) : -Infinity
  if (!Number.isNaN(synced) && synced > (Number.isNaN(base) ? -Infinity : base))
    return 'done'
  return now - request.at > REFRESH_MAX_WAIT_MS ? 'timed-out' : 'waiting'
}
